/**
 * BookmarksPanel - User bookmarks view.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { chatBookmarksApi } from '../../services/chatApi';
import { useChat } from '../../contexts/ChatContext';
import type { ChatBookmark } from '../../types/chat';

interface BookmarksPanelProps {
  onClose: () => void;
}

const BookmarksPanel: React.FC<BookmarksPanelProps> = ({ onClose }) => {
  const { t } = useTranslation('chat');
  const { channels, setCurrentChannel } = useChat();
  const [bookmarks, setBookmarks] = useState<ChatBookmark[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    chatBookmarksApi.list().then(setBookmarks).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleClick = useCallback((bookmark: ChatBookmark) => {
    const channel = channels.find(c => c.id === bookmark.channel_id);
    if (channel) {
      setCurrentChannel(channel);
      onClose();
    }
  }, [channels, setCurrentChannel, onClose]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="font-semibold text-sm">{t('bookmarks')}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
          </div>
        ) : bookmarks.length > 0 ? (
          bookmarks.map(bm => (
            <button
              key={bm.message_id}
              onClick={() => handleClick(bm)}
              className="w-full text-left px-4 py-3 border-b hover:bg-gray-50"
            >
              <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                <span>#{bm.channel_name}</span>
                <span>{new Date(bm.created_at).toLocaleDateString()}</span>
              </div>
              <div className="text-sm text-gray-800 line-clamp-2">{bm.content}</div>
              {bm.note && (
                <div className="text-xs text-gray-500 mt-1 italic">{bm.note}</div>
              )}
            </button>
          ))
        ) : (
          <div className="text-center text-gray-400 text-sm py-8">{t('noBookmarks')}</div>
        )}
      </div>
    </div>
  );
};

export default BookmarksPanel;
